import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { finalize, of, tap } from 'rxjs';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CreateAccountService } from './create-account.modal.service';
import { OperatorsService } from '../operators.service';

@Component({
  selector: 'app-create-account',
  templateUrl: './create-account.modal.component.html',
  styleUrls: ['./create-account.modal.component.scss'],
})
export class CreateAccountModalComponent implements OnInit {
  @ViewChild('requestPasswordInsert') requestPasswordInsert!: TemplateRef<any>;

  readonly separatorKeys = [ENTER, COMMA] as const;

  public isSaving = false;
  public validatingPassword = false;
  public errorMessage = '';
  public passwordInsert = '';
  private passModalRef?: NgbModalRef;

  public licensingsLoading = false;
  public licensingOptions: any[] = [];

  public banksLoading = false;
  public bankOptions: any[] = [];

  public platformsLoading = false;
  public platformOptions: any[] = [];

  public isProjectExpanded = true;
  public isCompanyExpanded = false;
  public isAddressExpanded = false;
  public isSponsorExpanded = false;
  public isPermissionsExpanded = false;
  public isListCnpjExpanded = false;
  public isIpExpanded = false;

  public preCnpj = new FormControl<string>('', { nonNullable: true });
  public isSearchingPre = false;
  private preAccountsCache: any[] | null = null;

  public estados = [
    'AC',
    'AL',
    'AP',
    'AM',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MT',
    'MS',
    'MG',
    'PA',
    'PB',
    'PR',
    'PE',
    'PI',
    'RJ',
    'RN',
    'RS',
    'RO',
    'RR',
    'SC',
    'SP',
    'SE',
    'TO',
  ];

  form = new FormGroup({
    operatorRegistration: new FormGroup({
      nameProject: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      site: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      licensingRegistrationId: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      bankProvider: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      currentPlatform: new FormControl<string>('', { nonNullable: true }),
      percentGGR: new FormControl<number>(0, { nonNullable: true }),
    }),

    company: new FormGroup({
      cnpj: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      razaoSocial: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      email: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      phone: new FormControl<string>('', { nonNullable: true }),
    }),

    adress: new FormGroup({
      logradouro: new FormControl<string>('', { nonNullable: true }),
      numero: new FormControl<string>('', { nonNullable: true }),
      complemento: new FormControl<string>('', { nonNullable: true }),
      cep: new FormControl<string>('', { nonNullable: true }),
      bairro: new FormControl<string>('', { nonNullable: true }),
      cidade: new FormControl<string>('', { nonNullable: true }),
      estado: new FormControl<string>('', { nonNullable: true }),
    }),

    sponsor: new FormGroup({
      cpf: new FormControl<string>('', { nonNullable: true }),
      name: new FormControl<string>('', { nonNullable: true }),
      email: new FormControl<string>('', { nonNullable: true }),
      phone: new FormControl<string>('', { nonNullable: true }),
    }),

    featureFlagRefundPix: new FormControl<boolean>(false, { nonNullable: true }),
    featureFlagCpfWithdraw: new FormControl<boolean>(false, { nonNullable: true }),
    featureFlagNewParam: new FormControl<boolean>(false, { nonNullable: true }),

    validateIp: new FormControl<boolean>(false, { nonNullable: true }),
    listsIp: new FormControl<string[]>([]),

    listCnpj: new FormGroup({
      flagCnpjCashIn: new FormControl<boolean>(false, { nonNullable: true }),
      flagCnpjCashOut: new FormControl<boolean>(false, { nonNullable: true }),
      cnpjCashIn: new FormControl<string[]>([]),
      cnpjCashOut: new FormControl<string[]>([]),
    }),
  });

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private createAccountService: CreateAccountService,
    private operatorsService: OperatorsService
  ) { }

  ngOnInit(): void {
    this.loadLicensings();
    this.loadBanks();
    this.loadPlatforms();

    this.form.controls.operatorRegistration.controls.currentPlatform.valueChanges.subscribe((name) => {
      const selected = this.platformOptions.find((p: any) => p.value === name);
      this.form.controls.listsIp.setValue(selected?.ips?.length ? [...selected.ips] : []);
    });
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  openSection(section: string): void {
    this.isProjectExpanded = section === 'project';
    this.isCompanyExpanded = section === 'company';
    this.isAddressExpanded = section === 'address';
    this.isSponsorExpanded = section === 'sponsor';
    this.isPermissionsExpanded = section === 'permissions';
    this.isListCnpjExpanded = section === 'listCnpj';
    this.isIpExpanded = section === 'ip';
  }

  private onlyDigits(v: string): string {
    return (v ?? '').toString().replace(/\D+/g, '');
  }

  buscarPreAccount(): void {
    if (this.isSearchingPre) return;

    const raw = (this.preCnpj.value ?? '').trim();
    const cnpj = this.onlyDigits(raw);

    if (!cnpj) {
      this.toastr.warning('Informe o CNPJ.', 'Aviso', {
        timeOut: 2000,
        positionClass: 'toast-top-right',
      });
      return;
    }

    this.isSearchingPre = true;

    const src$ = this.preAccountsCache
      ? of(this.preAccountsCache)
      : this.operatorsService.getPreAccounts().pipe(
        tap((list: any) => {
          this.preAccountsCache = (list ?? []) as any[];
        })
      );

    src$
      .pipe(finalize(() => (this.isSearchingPre = false)))
      .subscribe({
        next: (list: any) => {
          const arr = (list ?? []) as any[];

          const found =
            arr.find((x: any) => this.onlyDigits(x?.company?.cnpj ?? '') === cnpj) ||
            arr.find((x: any) => this.onlyDigits(x?.id ?? '') === cnpj);

          if (!found) {
            this.toastr.warning('Pré-cadastro não encontrado.', 'Aviso', {
              timeOut: 2500,
              positionClass: 'toast-top-right',
            });
            return;
          }

          this.applyPreAccount(found);

          this.toastr.success('Dados do pré-cadastro aplicados.', 'Sucesso', {
            timeOut: 2000,
            positionClass: 'toast-top-right',
          });
        },
        error: () => {
          this.toastr.error('Erro ao buscar pré-cadastro.', 'Erro', {
            timeOut: 3000,
            positionClass: 'toast-top-right',
          });
        },
      });
  }

  private applyPreAccount(item: any): void {
    const company = item?.company ?? {};
    const adress = item?.adress;
    const sponsor = item?.sponsor;

    const cnpj = (company?.cnpj ?? item?.id ?? '').toString();

    this.form.controls.company.patchValue({
      cnpj: cnpj,
      razaoSocial: (company?.razaoSocial ?? '').toString(),
      email: (company?.email ?? '').toString(),
      phone: (company?.phone ?? '').toString(),
    });

    if (adress) {
      this.form.controls.adress.patchValue({
        logradouro: (adress?.logradouro ?? '').toString(),
        numero: (adress?.numero ?? '').toString(),
        complemento: (adress?.complemento ?? '').toString(),
        cep: (adress?.cep ?? '').toString(),
        bairro: (adress?.bairro ?? '').toString(),
        cidade: (adress?.cidade ?? '').toString(),
        estado: (adress?.estado ?? '').toString().toUpperCase(),
      });
    }

    if (sponsor) {
      this.form.controls.sponsor.patchValue({
        cpf: (sponsor?.cpf ?? '').toString(),
        name: (sponsor?.name ?? '').toString(),
        email: (sponsor?.email ?? '').toString(),
        phone: (sponsor?.phone ?? '').toString(),
      });
    }

    this.form.controls.operatorRegistration.patchValue({
      site: (item?.site ?? '').toString(),
      licensingRegistrationId: (item?.licensingRegistrationId ?? '').toString(),
      bankProvider: (item?.bankProvider ?? '').toString(),
      currentPlatform: (item?.currentPlatform ?? '').toString(),
      percentGGR: Number(item?.percentGGR ?? 0) || 0,
    });
  }

  loadLicensings(): void {
    if (this.licensingsLoading) return;

    this.licensingsLoading = true;

    this.operatorsService
      .getLicensingRegistration(100, 1)
      .pipe(finalize(() => (this.licensingsLoading = false)))
      .subscribe({
        next: (value: any) => {
          const list = (value?.items ?? value?.data ?? value?.result ?? value ?? []) as any[];

          this.licensingOptions = (list ?? [])
            .map((l: any) => ({
              label: (l?.description ?? l?.razaoSocial ?? l?.cnpj ?? l?.id ?? '').toString(),
              value: (l?.id ?? '').toString(),
            }))
            .filter((x: any) => !!x.value);
        },
        error: () => {
          this.licensingOptions = [];
        },
      });
  }

  loadBanks(): void {
    if (this.banksLoading) return;

    this.banksLoading = true;

    this.createAccountService
      .getEnum()
      .pipe(finalize(() => (this.banksLoading = false)))
      .subscribe({
        next: (value: any) => {
          const list = (value?.preBankProviderName ?? []) as string[];

          this.bankOptions = (list ?? [])
            .map((b: string) => ({
              label: (b ?? '').toString(),
              value: (b ?? '').toString(),
            }))
            .filter((x: any) => !!x.value);
        },
        error: () => {
          this.bankOptions = [];
        },
      });
  }

  loadPlatforms(): void {
    if (this.platformsLoading) return;

    this.platformsLoading = true;

    this.operatorsService
      .getPlatform(100, 1)
      .pipe(finalize(() => (this.platformsLoading = false)))
      .subscribe({
        next: (value: any) => {
          const list = (value?.tickets ?? value?.items ?? value?.data ?? []) as any[];

          this.platformOptions = (list ?? [])
            .map((p: any) => ({
              label: (p?.name ?? '').toString(),
              value: (p?.name ?? '').toString(),
              ips: Array.isArray(p?.ips) ? p.ips : [],
            }))
            .filter((x: any) => !!x.value);
        },
        error: () => {
          this.platformOptions = [];
        },
      });
  }

  addCnpj(event: MatChipInputEvent, which: 'in' | 'out'): void {
    const value = (event.value || '').trim();
    event.chipInput?.clear();

    if (!value) return;

    const ctrl =
      which === 'in'
        ? this.form.controls.listCnpj.controls.cnpjCashIn
        : this.form.controls.listCnpj.controls.cnpjCashOut;

    const current = [...(ctrl.value ?? [])];

    if (current.includes(value)) {
      this.toastr.warning('CNPJ já adicionado.', 'Aviso', {
        timeOut: 2000,
        positionClass: 'toast-top-right',
      });
      return;
    }

    current.push(value);
    ctrl.setValue(current);
  }

  removeCnpj(value: string, which: 'in' | 'out'): void {
    const ctrl =
      which === 'in'
        ? this.form.controls.listCnpj.controls.cnpjCashIn
        : this.form.controls.listCnpj.controls.cnpjCashOut;

    const current = [...(ctrl.value ?? [])];
    ctrl.setValue(current.filter((x) => x !== value));
  }

  save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.toastr.warning('Corrija os campos inválidos antes de salvar.', 'Aviso', {
        timeOut: 2500,
        positionClass: 'toast-top-right',
      });
      return;
    }

    this.openPasswordModal();
  }

  openPasswordModal(): void {
    this.errorMessage = '';
    this.passwordInsert = '';

    this.passModalRef = this.modalService.open(this.requestPasswordInsert, {
      backdrop: 'static',
      keyboard: true,
      centered: true,
      size: 'sm',
    });
  }

  confirmPasswordInsert(): void {
    if (this.validatingPassword || this.isSaving) return;

    const pass = (this.passwordInsert ?? '').toString().trim();
    if (!pass) {
      this.errorMessage = 'Informe a senha.';
      return;
    }

    const payload = this.buildPayload(pass);

    this.validatingPassword = true;
    this.isSaving = true;

    this.createAccountService
      .createAccount(payload)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.validatingPassword = false;
        })
      )
      .subscribe({
        next: () => {
          this.passModalRef?.close();
          this.toastr.success('Conta criada com sucesso!', 'Sucesso', {
            timeOut: 3000,
            positionClass: 'toast-top-right',
          });

          this.activeModal.close({
            saved: true,
            cnpj: payload?.company?.cnpj ?? '',
          });
        },
        error: () => {
          this.errorMessage = 'Erro ao validar senha ou criar conta.';
        },
      });
  }

  private buildPayload(passwordInsert: string): any {
    const v = this.form.getRawValue();

    return {
      personType: 0,
      getSplit: false,
      operatorRegistration: {
        ...(v.operatorRegistration ?? {}),
        percentGGR: Number(v.operatorRegistration?.percentGGR ?? 0) || 0,
      },
      company: {
        ...(v.company ?? {}),
        cnpj: this.onlyDigits(v.company?.cnpj),
      },
      adress: v.adress ?? {},
      sponsor: {
        ...(v.sponsor ?? {}),
        cpf: this.onlyDigits(v.sponsor?.cpf),
      },
      featureFlagRefundPix: !!v.featureFlagRefundPix,
      featureFlagCpfWithdraw: !!v.featureFlagCpfWithdraw,
      featureFlagNewParam: !!v.featureFlagNewParam,
      listCnpj: {
        ...(v.listCnpj ?? {}),
        flagCnpjCashIn: !!v.listCnpj?.flagCnpjCashIn,
        flagCnpjCashOut: !!v.listCnpj?.flagCnpjCashOut,
        cnpjCashIn: v.listCnpj?.cnpjCashIn ?? [],
        cnpjCashOut: v.listCnpj?.cnpjCashOut ?? [],
      },
      listsIp: (v.listsIp ?? []).map((x: string) => (x ?? '').trim()).filter(Boolean),
      passwordInsert: passwordInsert,
    };
  }

  addIp(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    event.chipInput?.clear();

    if (!value) return;

    const ctrl = this.form.controls.listsIp;
    const current = [...(ctrl.value ?? [])];

    if (current.includes(value)) {
      this.toastr.warning('IP já adicionado.', 'Aviso', {
        timeOut: 2000,
        positionClass: 'toast-top-right',
      });
      return;
    }

    current.push(value);
    ctrl.setValue(current);
  }

  removeIp(value: string): void {
    const ctrl = this.form.controls.listsIp;
    const current = [...(ctrl.value ?? [])];
    ctrl.setValue(current.filter((x) => x !== value));
  }

  async pasteDocument(): Promise<void> {
    try {
      const text = (await navigator.clipboard.readText()).trim();
      this.preCnpj.setValue(text);
    } catch { }
  }
}
